import moment from 'moment-timezone';
import _uniqBy from 'lodash/uniqBy';
import { isNaN, isNumber } from 'lodash';
import { formatTimeZoneLabel } from '@jjj/components';
import type { ThemeProviderLocale } from '@jjj/move';
import { COUNTRIES } from './countries';
import { getCurrentCountryContext } from './countriesHelpers';
import { CURRENT_TIMEZONE_KEY } from './localStorageKeys.const';
import { getCachedCountriesSettings } from '../redux/countriesSettings/helpers';
import type { TemporalFormats, TimeFormats } from '../redux/countriesSettings/helpers.d';
import { logger } from './logger';
import { t } from '../i18n';

moment.relativeTimeThreshold('m', 60);

let currentTimezone = localStorage.getItem(CURRENT_TIMEZONE_KEY) || getCurrentCountryContext().timezone;

// Helper function to get date format from countries_settings
export function getDateFormat(formatKey?: keyof TemporalFormats): string {
  // Read from cache instead of calling store.getState() (which fails during reducer execution)
  const countriesSettings = getCachedCountriesSettings();

  if (countriesSettings?.temporal_formats && formatKey && countriesSettings.temporal_formats[formatKey]) {
    return countriesSettings.temporal_formats[formatKey];
  }

  return 'MM/DD/YYYY';
}

// Helper function to get time format from countries_settings
export function getTimeFormat(formatKey?: keyof TimeFormats): string {
  // Read from cache instead of calling store.getState() (which fails during reducer execution)
  const countriesSettings = getCachedCountriesSettings();

  if (countriesSettings?.temporal_formats && formatKey && countriesSettings.temporal_formats[formatKey]) {
    return countriesSettings.temporal_formats[formatKey];
  }

  return 'hh:mm A';
}
export function isDateString(date: string) {
  return (
    typeof date === 'string' &&
    (date.match(/^\d\d\d\d-\d\d-\d\d$/) ||
      date.match(/^\d\d-\d\d-\d\d\d\d$/) ||
      date.match(/^\d\d\/\d\d\/\d\d\d\d$/) ||
      date.match(/^\d\d\d\d\/\d\d\/\d\d$/))
  );
}

function setCurrentTimezone(zone?: string) {
  if (!currentTimezone) {
    currentTimezone = zone || moment.tz.guess(true) || getCurrentCountryContext().timezone;
    localStorage.setItem(CURRENT_TIMEZONE_KEY, currentTimezone);
  }
}

export function updateCurrentTimezone(zone: string) {
  setCurrentTimezone(zone);

  if (zone) {
    currentTimezone = zone;
    localStorage.setItem(CURRENT_TIMEZONE_KEY, zone);
  }
}

export function withTZ(date?: string | Date, format?: string) {
  setCurrentTimezone();
  const value = date ? moment(date, format) : moment();

  return value.tz(currentTimezone);
}

// Start-of-day for a date-only value, anchored in the current timezone. Unlike `withTZ`,
// which parses in the browser's local zone and can roll the date back a day.
export function startOfDayInCurrentTZ(date: string, format: string) {
  setCurrentTimezone();

  return moment.tz(date, format, currentTimezone);
}

export function withUTC(date: string | Date, format?: string) {
  setCurrentTimezone();
  const value = date ? moment(date, format) : moment();

  return value.utc();
}

export function getFormattedDate({
  date,
  formatKey,
  timeZone = currentTimezone,
  noLocalTimeZone = false,
}: {
  date?: string | Date | moment.Moment;
  formatKey: keyof TemporalFormats | keyof TimeFormats;
  timeZone?: string;
  noLocalTimeZone?: boolean;
}): string {
  if (!date) {
    return '';
  }

  setCurrentTimezone();

  let momentDate: moment.Moment;

  if (moment.isMoment(date)) {
    momentDate = date;
  } else {
    momentDate = moment(date);
  }

  if (!momentDate.isValid()) {
    return '';
  }

  let dateToFormat = momentDate;

  if (!noLocalTimeZone && timeZone) {
    dateToFormat = momentDate.tz(timeZone);
  }

  const format = getDateFormat(formatKey as keyof TemporalFormats);

  return dateToFormat.format(format);
}

export function formatCalendarDate(date?: string | null): string {
  return date ? getFormattedDate({ date, formatKey: 'date_numeric_long', noLocalTimeZone: true }) : '';
}

export function getFormattedTime({
  date,
  formatKey,
  timeZone = currentTimezone,
}: {
  date?: string | Date | moment.Moment;
  formatKey: keyof TimeFormats;
  timeZone?: string;
}): string {
  return getFormattedDate({ date, formatKey, timeZone });
}

export function getCurrentTimezone() {
  setCurrentTimezone();

  return currentTimezone;
}

function getTimeZone() {
  return getCurrentTimezone();
}

function getTimezoneCity(tzCode: string) {
  const tzPath = (tzCode || '').split('/');

  return (tzPath[2] || tzPath[1] || '').replace(/_/g, ' ');
}

function getTimezoneLabel(tzCode: string) {
  setCurrentTimezone();

  return `(UTC ${moment.tz(tzCode).format('Z')}) ${getTimezoneCity(tzCode)}`;
}

export function getFormattedLocationTimezone(locationTimezone?: string | null): string | null {
  return locationTimezone ? formatTimeZoneLabel(locationTimezone, false) : null;
}

function getTimezoneOption(tzCode: string) {
  return {
    offset: moment.tz(tzCode).format('Z'),
    name: getTimezoneLabel(tzCode),
    id: tzCode,
  };
}

function getCurrentTimezoneOption() {
  return getTimezoneOption(currentTimezone);
}

export function getTimezones() {
  const timezones = moment.tz.names().map(getTimezoneOption);
  timezones.unshift(getCurrentTimezoneOption());

  return _uniqBy(timezones, 'id');
}

/**
 * @param {string} countryCode !important this should be 2 digit code like US, CA, IL
 * @returns {Array}
 */
export function getTimezonesForCountry(countryCode: string) {
  if (!countryCode) {
    return getTimezones();
  }

  const timezones = moment.tz.zonesForCountry(countryCode);

  if (!timezones) {
    return getTimezones();
  }

  const timezoneOptions = timezones.map(getTimezoneOption);

  return _uniqBy(timezoneOptions, 'id');
}

export const setDefaultTimezoneField = () => {
  return {
    name: 'time-zone',
    touched: false,
    error: '',
    value: getTimezonesForCountry(getCurrentCountryContext().alpha_2_code)[0].id,
  };
};

export const calculateDuration = (date: string) => {
  let duration = null;

  if (date) {
    const diff = Math.abs(moment().diff(date));
    const milisecondDuration = moment.duration(diff);

    duration = milisecondDuration.humanize();
  }

  return duration;
};

export const calculateDurationByMinutes = (minutesDuration: number) => {
  const hours = Math.floor(minutesDuration / 60);
  const minutes = minutesDuration % 60;
  const hoursLabel = hours === 1 ? 'dash.common.time.hours' : 'dash.common.time.hours_plural';
  const minutesLabel = minutes === 1 ? 'dash.common.time.mins' : 'dash.common.time.mins_plural';

  if (hours > 0 && minutes > 0) {
    return `${t(hoursLabel, { count: hours })} ${t(minutesLabel, { count: minutes })}`;
  }

  if (hours > 0) {
    return t(hoursLabel, { count: hours });
  }

  return t(minutesLabel, { count: minutes });
};

export const formatDurationFromMinutes = (minutesDuration: number) => {
  const hours = Math.floor(minutesDuration / 60);
  const minutes = minutesDuration % 60;

  if (hours > 0 && minutes > 0) {
    return t('dash.common.time.hours_minutes_short', { hours, minutes });
  }

  if (hours > 0) {
    return t(hours === 1 ? 'dash.common.time.hours_long' : 'dash.common.time.hours_long_plural', { count: hours });
  }

  return t(minutes === 1 ? 'dash.common.time.mins' : 'dash.common.time.mins_plural', { count: minutes });
};

export const formatDurationFromSeconds = (seconds: number) => {
  let formattedDuration = '';

  if (seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours >= 1) {
      formattedDuration = `${hours}${minutes ? `:${minutes}` : ''}h`;
    } else if (minutes >= 1) {
      formattedDuration = `${minutes}m`;
    } else {
      formattedDuration = `${seconds}s`;
    }
  }

  return formattedDuration;
};

export const is12HoursTimeFormat = () => {
  const currentCountry = getCurrentCountryContext();

  return currentCountry.code !== COUNTRIES.ISR.code && currentCountry.code !== COUNTRIES.GBR.code;
};

// Whether a date pattern writes the day before the month, single-letter tokens ("D/M/YYYY") included.
const isDayFirstDateFormat = (format: string): boolean => {
  const day = format.toUpperCase().indexOf('D');
  const month = format.toUpperCase().indexOf('M');

  return day !== -1 && (month === -1 || day < month);
};

// Israel reads dates day-first but opens its week on Sunday, which `en-gb` would move to Monday while the rest
// of the app stays Sunday-first. The 24-hour default is not relied on either: every time picker names `ampm`.
export const getPickerLocale = (): ThemeProviderLocale => {
  if (getCurrentCountryContext().code === COUNTRIES.ISR.code) return 'en';

  return isDayFirstDateFormat(getDateFormat('date_numeric_long')) ? 'en-gb' : 'en';
};

export function setFirstDayOfWeek() {
  const locale = moment.locale();
  moment.updateLocale(locale, {
    week: {
      dow: locale === 'he' ? 0 : 1, // set first day of week to Sunday for Israel
    },
  });
}

export function extractDateFromDateTime(dateTime: string) {
  try {
    const dt = new Date(dateTime);

    if (isNaN(dt)) {
      throw new Error('Invalid datetime format');
    }

    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');

    const dateOnly = `${year}-${month}-${day}`;

    return dateOnly;
  } catch (error) {
    logger.error(error);

    return null;
  }
}

export function convertTimeTo12HourFormat(time24h: string) {
  const timeObj = moment(time24h, 'HH:mm:ss');

  return timeObj.format('h:mm A');
}

// The organisation's clock with the hour always two digits on a 24-hour one ("H:mm" reads "06:00"); a 12-hour pattern
// is kept as is.
const wallTimePattern = (): string => getTimeFormat('time_hours_minutes').replace(/(^|[^H])H(?!H)/, '$1HH');

// Prints an "HH:mm" wire time on the organisation's clock. A value that will not parse is echoed as sent so a
// summary never loses it; `strict` answers '' for it and also refuses loose shapes ("06:00:00", "6", "6:00").
export function formatWallTime(hhmm: string | null | undefined, { strict = false } = {}): string {
  if (!hhmm) return '';
  const parsed = moment(hhmm, 'HH:mm', strict);

  if (parsed.isValid()) return parsed.format(wallTimePattern());

  return strict ? '' : hhmm;
}

// Reads a shift break as whole minutes: hours as a number, or the API label ("45 mins", "2 hrs", "1:05 hrs").
// Null when the label cannot be read, so the caller can leave the stored break alone.
export const convertBreakDurationToMinutes = (duration?: string | number | null) => {
  if (isNumber(duration)) {
    return Math.round(duration * 60);
  }

  const match = duration?.match(/^(\d+)(?::(\d{2}))?\s+(hrs|mins)$/);

  if (!match) {
    return null;
  }

  const [, whole, minutes, unit] = match;

  return unit === 'hrs' ? Number(whole) * 60 + Number(minutes || 0) : Number(whole);
};

// The shift keeps the break in hours with two decimals and labels it by rounding down, so round up:
// 20 mins is sent as 0.34 h and reads back as "20 mins", where 0.33 h would read "19 mins".
export const convertBreakMinutesToHours = (minutes: number) => Math.ceil((minutes * 100) / 60) / 100;

export const aYearFromToday = new Date(moment().add(1, 'years').format('YYYY-MM-DDTHH:mm:ss'));

export const convertDateToTime = (dateUTC: string) => {
  if (is12HoursTimeFormat()) {
    return moment.tz(dateUTC, getTimeZone()).format('hh:mmA');
  }

  return moment.tz(dateUTC, getTimeZone()).format('HH:mm');
};

export const formatToISOStringWithMoment = (date: string, time: string, timezone?: string) => {
  if (!date || !time) return null;

  const dateTimeString = `${date} ${time}`;
  const tz = timezone || getCurrentTimezone();

  return moment.tz(dateTimeString, 'YYYY-MM-DD hh:mma', tz).utc().toISOString();
};

export const convertMinutesToHoursString = (minutes: number) => String(Number((minutes / 60).toFixed(2)));

// Known defect: the date is parsed at the browser's midnight before moving into `currentTimezone`,
// so a zone west of the machine's loses today. Fixing it here also moves agency-contract `starts_on`.
export function isDateInFutureOrToday(dateString: string) {
  const inputDate = withTZ(dateString);
  const now = withTZ();

  return inputDate.startOf('day').isSameOrAfter(now.startOf('day'));
}
